import { create } from "zustand";
import { createStreamController } from "./conversation/stream/streamController";
import useChatStreamStore from "./useChatStreamStore";
import { critical as _crit } from "../../utils/ChatStreamLogger";
import {
  _isTerminalForStreaming,
  _isEmptyAssistantStreamMessage,
} from "./agentStoreUtils";
import {
  _markChatStatusReason,
  _setChatStatusReasonSink,
} from "./agentStreamLifecycle";
import { setLiveStreamController } from "./agentStreamController";
import { createAgentsSlice } from "./agentStoreAgentsSlice";
import { createSessionsSlice } from "./agentStoreSessionsSlice";
import { createMessagesSlice } from "./agentStoreMessagesSlice";
import { createConversationSlice } from "./agentStoreConversationSlice";
import { createHumanInteractionSlice } from "./agentStoreHumanInteractionSlice";
import { createStreamSlice } from "./agentStoreStreamSlice";

const useAgentStore = create((set, get) => ({
  ...createAgentsSlice(set, get),
  ...createSessionsSlice(set, get),
  ...createMessagesSlice(set, get),
  ...createConversationSlice(set, get),
  ...createHumanInteractionSlice(set, get),
  ...createStreamSlice(set, get),

  error: null,
  sendError: null,
  clearError: () => { get()._applyLifecycle({ kind: 'optimistic', sessionId: null, action: 'clearError' }); set({ sendError: null }); },
}));

// Wire the chatStatus-transition reason channel into store state. Actions call
// _markChatStatusReason(reason) just before the set() that drives a transition;
// the sink stamps state.chatStatusReason, which the transition-logging
// subscription below reads. Keeping the reason in state (vs a module global)
// makes it inspectable in devtools and resettable in tests.
_setChatStatusReasonSink((reason) => useAgentStore.setState({ chatStatusReason: reason }));

// The live-stream controller owns the reducer cursors, the intra-frame flush
// buffer, and the animation-frame throttle for SSE ui_chunk events. It is wired
// with the store accessors and the store-owned helpers it must call back into.
const streamController = createStreamController({
  getState: () => useAgentStore.getState(),
  setState: (patch) => useAgentStore.setState(patch),
  isTerminalForStreaming: _isTerminalForStreaming,
  markChatStatusReason: _markChatStatusReason,
  isEmptyAssistantStreamMessage: _isEmptyAssistantStreamMessage,
});
setLiveStreamController(streamController);

// Bridge the SSE connection state into the lifecycle reducer's sseAuthority
// flag (§4.8), which gates the REST authority rule (§4.3 restSupersededByLiveSse).
// We subscribe to the stream store's sessionStreamStateBySessionId rather than a
// single transport callback because _setSessionStreamState is the one chokepoint
// for EVERY transition — connecting/connected/reconnecting plus the
// disconnectStream→idle and connectionFailed→disconnected paths the transport's
// onConnectionStateChange does not emit. The reducer's session gate (§4.1) drops
// connState for sessions other than the one the lifecycle currently tracks.
// Guarded behind a window-scoped flag so HMR / double module evaluation doesn't
// register duplicate subscribers.
const _STREAM_STATE_SUBSCRIPTION_KEY = '__useAgentStoreStreamStateSubscription__';
if (typeof window !== 'undefined' && !window[_STREAM_STATE_SUBSCRIPTION_KEY]) {
  let _prevStreamMap = useChatStreamStore.getState().sessionStreamStateBySessionId;
  window[_STREAM_STATE_SUBSCRIPTION_KEY] = useChatStreamStore.subscribe((state) => {
    const next = state.sessionStreamStateBySessionId;
    if (next === _prevStreamMap) return;
    const prev = _prevStreamMap;
    _prevStreamMap = next;
    for (const sessionId of Object.keys(next)) {
      if (prev[sessionId] !== next[sessionId]) {
        useAgentStore.getState()._applyLifecycle({
          kind: 'connState',
          sessionId,
          state: next[sessionId],
        });
      }
    }
  });
}

// Always-on logging of chatStatus transitions. Key signal for diagnosing
// "chat stuck on in_progress" — tells you exactly when (and if) the UI moved
// submitted → streaming → ready, or got stuck. Guarded behind a window-scoped
// flag so HMR / double module evaluation doesn't register duplicate subscribers
// (which would emit each transition log N times).
const _CHAT_STATUS_SUBSCRIPTION_KEY = '__useAgentStoreChatStatusSubscription__';
if (typeof window !== 'undefined' && !window[_CHAT_STATUS_SUBSCRIPTION_KEY]) {
  let _prevChatStatus = useAgentStore.getState().chatStatus;
  window[_CHAT_STATUS_SUBSCRIPTION_KEY] = useAgentStore.subscribe((state) => {
    const next = state.chatStatus;
    if (next !== _prevChatStatus) {
      const level = next === 'error' ? 'error' : 'info';
      const reason = state.chatStatusReason || 'unknown';
      _crit("AgentStore", level, `chatStatus ${_prevChatStatus} → ${next} (reason=${reason})`);

      if (next === 'streaming') {
        const streamState = useChatStreamStore.getState().sessionStreamStateBySessionId;
        const sid = state.sessionDetail?.chatSessionId;
        const cs = sid ? streamState[sid] : null;
        if (cs !== 'connected' && cs !== 'connecting') {
          _crit(
            "AgentStore",
            "error",
            `PHANTOM STREAMING — chatStatus=streaming but SSE is ${cs || 'idle'} for session=${sid}. reason=${reason}`
          );
        }
      }

      _prevChatStatus = next;
    }
  });
}

export default useAgentStore;
